import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "../assets/icons/Icon";
import LocationIcon from "../../src/assets/icons/location-icon.svg"
import { OP_MODE } from "../utils/constants";
import { useIsWriteRestrictedUser } from "../hooks/useIsWriteRestrictedUser";
import { unitListReturnState } from "../utils/unitListReturn";
import Button from "./Common/Button";
import AlertIcon from "./Common/AlertIcon";
import ConnectionStatusIcon from "./Common/ConnectionStatusIcon";

const ViewUnitCardComponent = (props) => {
  const { metadata, serialNumber, status } = props?.info;
  const {
    setSelectedUnits,
    handleEditUnit,
    handleRemoveUnit,
    activeDropdownId,
    setActiveDropdownId,
    handleGenerateReport,
  } = props;

  const isActionsDisabled = useIsWriteRestrictedUser();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const alertIconRef = useRef(null);
  const alertTooltipRef = useRef(null);
  const [alertTooltipDirection, setAlertTooltipDirection] = useState("down");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdownId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setActiveDropdownId]);

  const activeAlerts = Array.isArray(props?.info?.activeAlerts?.alerts)
    ? props.info.activeAlerts.alerts
    : [];
  const hasActiveAlerts = activeAlerts.length > 0;
  const severity = activeAlerts.reduce((acc, alert) => {
    if (alert?.severity === "critical") {
      return "critical";
    }
    if (alert?.severity === "warning" && acc !== "critical") {
      return "warning";
    }
    return acc;
  }, "maintenance");

  const handleAlertTooltipPosition = () => {
    if (!alertIconRef.current || !alertTooltipRef.current) return;

    const iconRect = alertIconRef.current.getBoundingClientRect();
    const tooltipHeight = alertTooltipRef.current.offsetHeight;
    const spaceAbove = iconRect.top;
    const spaceBelow = window.innerHeight - iconRect.bottom;

    if (spaceBelow < tooltipHeight + 12 && spaceAbove > spaceBelow) {
      setAlertTooltipDirection("up");
      return;
    }

    setAlertTooltipDirection("down");
  };

  return (
    <div
      className="units bg-white dark:darkThemeGradiantBg p-4 rounded-lg shadow-appShadow min-h-56"
      data-testid={`unit-card-${serialNumber}`}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className="unitsBody flex grow cursor-pointer"
          onClick={() => navigate(`/units/${serialNumber}`, { state: unitListReturnState("units", location.search) })}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              navigate(`/units/${serialNumber}`, { state: unitListReturnState("units", location.search) });
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex flex-col gap-3 w-full">
            <div className="flex flex-col w-full">
              <p title={metadata?.name} className="podName text-dark-grey dark:text-white font-semibold text-base leading-5 lg:text-lg lg:leading-7 tracking-[0.27px] w-full truncate" data-testid={`unit-${serialNumber}-name`}>
                {metadata?.name}
              </p>
              <p className="text-dark-grey dark:text-white text-sm leading-5 font-normal tracking-[0.021px]">
                {serialNumber}
              </p>
            </div>
            <div className="flex items-center gap-10 w-full">
              <div className="podLocation flex items-start gap-2 w-full">
                {/* <LocationIcon width={"16"} height={"i6"} /> */}
                <div className="iocnHolder flex shrink-0 pt-1">
                  <Icon src={LocationIcon} alt="LocationIcon" className="w-4 h-4" />
                </div>
                <p 
                className="text-dark-grey dark:text-white max-w-[75%] text-sm leading-6 font-medium tracking-[0.021px] w-full line-clamp-2 text-ellipsis"
                title={[metadata?.propertyName, metadata?.buildingName, metadata?.unit, metadata?.floor ? `Floor ${metadata.floor}` : null, metadata?.room ? `Room ${metadata.room}` : null]
                      .filter(Boolean)
                      .join(", ")}
                >
                  <span className="truncate">
                    {[metadata?.propertyName, metadata?.buildingName, metadata?.unit]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                  <br />
                  <span>
                    {[metadata?.floor ? `Floor ${metadata.floor}` : null, metadata?.room ? `Room ${metadata.room}` : null]
                      .filter(Boolean)
                      .join(", ") || <>&nbsp;</>}
                  </span>
                </p>

              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center min-h-4">
                {hasActiveAlerts && (
                  <div
                    className="tooltipHolder relative group"
                    onMouseEnter={handleAlertTooltipPosition}
                  >
                 <AlertIcon severity={severity} data-testid={`alert-icon-${serialNumber}`} />
                    <div
                      ref={alertTooltipRef}
                      className={`tooltip bg-black rounded-md text-white text-sm px-2 py-1 absolute left-0 z-10 w-72 whitespace-normal invisible opacity-0 group-hover:visible group-hover:opacity-100 ${alertTooltipDirection === "up" ? "bottom-7" : "top-7"}`}
                      data-testid={`unit-${serialNumber}-alert-tooltip`}
                    >
                      {activeAlerts.map((alert, index) => (
                        <div
                          key={`${alert?.type || "alert"}-${index}`}
                          className={index === activeAlerts.length - 1 ? "" : "border-b border-white/20 pb-2 mb-2"}
                        >
                          <p><span className="font-semibold">Type:</span> {alert?.type || "--"}</p>
                          <p><span className="font-semibold">Severity:</span> {alert?.severity || "--"}</p>
                          <p><span className="font-semibold">Message:</span> {alert?.message || "--"}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-start gap-6">
                <div className="flex flex-col">
                  <div className="infoLabel text-dark-grey dark:text-white text-xs leading-4 font-normal tracking-[0.018px]">
                    Control Source
                  </div>
                  <div className="infoValue text-dark-grey dark:text-white font-medium text-sm leading-6 tracking-[0.021px]">
                    {status?.controlSource || "--"}
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="infoLabel text-dark-grey dark:text-white text-xs leading-4 font-normal tracking-[0.018px]">
                    Schedule Source
                  </div>
                  <div className="infoValue text-dark-grey dark:text-white font-medium text-sm leading-6 tracking-[0.021px]">
                    {status?.scheduleSource || "--"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="unitsFooter border-t border-grey dark:border-darkThemeBorder basis-9">
          <div className="flex items-center justify-between h-full pt-3">
            <div className="flex items-center gap-6">
              <ConnectionStatusIcon
                connected={status?.connected === true}
                connectionType={status?.connectionType}
                data-testid={`connection-status-${serialNumber}`}
              />

              <div className="tooltipHolder flex items-center gap-2 relative group">
                {status?.running === true ? (
                  <>
                    <div className="powerHolder">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2V6"
                          stroke="#00A653"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M8.5 3.70605C5.26806 5.07157 3 8.27099 3 12.0001C3 16.9707 7.02944 21.0001 12 21.0001C16.9706 21.0001 21 16.9707 21 12.0001C21 8.27099 18.7319 5.07157 15.5 3.70605"
                          stroke="#00A653"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Power On
                    </span>
                  </>
                ) : status?.running === false ? (
                  <>
                    <div className="powerHolder">
                       <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2V6"
                          stroke="#AFC3BC"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M8.5 3.70605C5.26806 5.07157 3 8.27099 3 12.0001C3 16.9707 7.02944 21.0001 12 21.0001C16.9706 21.0001 21 16.9707 21 12.0001C21 8.27099 18.7319 5.07157 15.5 3.70605"
                          stroke="#AFC3BC"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Power Off
                    </span>
                  </>
                ) : (
                  <>
                    <div className="powerHolder">
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2V6"
                          stroke="#AFC3BC"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M8.5 3.70605C5.26806 5.07157 3 8.27099 3 12.0001C3 16.9707 7.02944 21.0001 12 21.0001C16.9706 21.0001 21 16.9707 21 12.0001C21 8.27099 18.7319 5.07157 15.5 3.70605"
                          stroke="#AFC3BC"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Power Unknown
                    </span>
                  </>
                )}
              </div>

              <div className="tooltipHolder flex items-center gap-2 relative group">
                {status?.mode === OP_MODE.DRY && (
                  <>
                    <div className="dryHolder">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        viewBox="0 0 41 40"
                        fill="none"
                      >
                        <path
                          d="M20.4799 4.16699C18.5078 6.23898 11.7324 13.6955 11.7324 19.7451C11.7324 24.5775 15.6474 28.4951 20.4799 28.4951C25.3124 28.4951 29.2299 24.5775 29.2299 19.7451C29.2299 13.6955 22.452 6.23898 20.4799 4.16699Z"
                          stroke="#3B82F6"
                          strokeWidth="2.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.3965 31.6621L27.5632 8.32878"
                          stroke="#F87171"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Dry
                    </span>
                  </>
                )}

                {status?.mode === OP_MODE.FAN_ONLY && (
                  <>
                    <div className="fanHolder">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M3 8H9.5C10.8807 8 12 6.88071 12 5.5C12 4.11929 10.8807 3 9.5 3C8.11929 3 7 4.11929 7 5.5V5.85714"
                          stroke="#00BBDD"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M4 14H18.5C20.433 14 22 15.567 22 17.5C22 19.433 20.433 21 18.5 21C16.567 21 15 19.433 15 17.5V17"
                          stroke="#00BBDD"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M2 11H18.5C20.433 11 22 9.433 22 7.5C22 5.567 20.433 4 18.5 4C16.567 4 15 5.567 15 7.5V8"
                          stroke="#00BBDD"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Fan
                    </span>
                  </>
                )}

                {status?.mode === OP_MODE.HEAT && (
                  <>
                    <div className="heatHolder">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="5"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M12 2V4"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M12 20V22"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M4 12L2 12"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M22 12L20 12"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M19.7778 4.22266L17.5558 6.25424"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M4.22217 4.22266L6.44418 6.25424"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M6.44434 17.5557L4.22211 19.7779"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M19.7778 19.7773L17.5558 17.5551"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Heat
                    </span>
                  </>
                )}

                {status?.mode === OP_MODE.COOL && (
                  <>
                    <div className="coolHolder">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2V18M12 22V18M12 18L15 21M12 18L9 21M15 3L12 6L9 3"
                          stroke="#0099FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M3.33978 7.00042L6.80389 9.00042M6.80389 9.00042L17.1962 15.0004M6.80389 9.00042L5.70581 4.90234M6.80389 9.00042L2.70581 10.0985M17.1962 15.0004L20.6603 17.0004M17.1962 15.0004L21.2943 13.9023M17.1962 15.0004L18.2943 19.0985"
                          stroke="#0099FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M20.6602 7.00042L17.1961 9.00042M17.1961 9.00042L6.80376 15.0004M17.1961 9.00042L18.2941 4.90234M17.1961 9.00042L21.2941 10.0985M6.80376 15.0004L3.33966 17.0004M6.80376 15.0004L2.70569 13.9023M6.80376 15.0004L5.70569 19.0985"
                          stroke="#0099FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Cool
                    </span>
                  </>
                )}

                {status?.mode === OP_MODE.HEAT_AND_COOL && (
                  <>
                    <div className="heatCoolHolder">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                      >
                        <path
                          d="M14 2V18M14 22V18M14 18L17 21M14 6L17 3"
                          stroke="#0099FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M19.1963 15L22.6604 17M19.1963 15L23.2944 13.9019M19.1963 15L20.2944 19.0981M19.1963 15L14.0001 12"
                          stroke="#0099FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M22.6601 7.00042L19.196 9.00042M19.196 9.00042L20.2941 4.90234M19.196 9.00042L23.2941 10.0985M19.196 9.00042L13.9999 12.0004"
                          stroke="#0099FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M11 12C11 14.7614 11 14.5 11 17C8.23858 17 6 14.7614 6 12C6 9.23858 8.23858 7 11 7C11 8.5 11 9.23858 11 12Z"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M11 2V4"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M11 20V22"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M3 12L1 12"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M3.22217 4.22266L5.44418 6.25424"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M5.44434 17.5557L3.22211 19.7779"
                          stroke="#FE5B26"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Heat and Cool
                    </span>
                  </>
                )}
                </div>
                {/* --- Setpoint values (conditional) --- */}
                <div className="tooltipHolder flex items-center gap-2 relative group">
                {status?.connected && (
                  <>
                    {status?.mode === OP_MODE.HEAT && (
                      <>
                      <span className="text-[#FE5B26]">{status?.heatTargetTempF ? status?.heatTargetTempF + "°F" : "--"}</span>
                      <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Heat Setpoint
                    </span>
                      </>
                    )}
                    {status?.mode === OP_MODE.COOL && (
                      <>
                      <span className="text-[#0099FF]">{status?.coolTargetTempF ? status?.coolTargetTempF + "°F" : "--"}</span>
                      <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Cool Setpoint
                    </span>
                      </>
                    )}
                    {status?.mode === OP_MODE.HEAT_AND_COOL && (
                      <>
                        <span className="text-[#FE5B26]">{status?.heatTargetTempF ? status?.heatTargetTempF + "°F" : "--"}</span>
                        <span className="text-[#0099FF]">{status?.coolTargetTempF ? status?.coolTargetTempF + "°F" : "--"}</span>
                        <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Heat & Cool Setpoint
                    </span>
                      </>
                    )}
                    {status?.mode === OP_MODE.DRY && (
                      <>
                      <span className="text-[#0099FF]">{status?.coolTargetTempF ? status?.coolTargetTempF + "°F" : "--"}</span>
                      <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
                      Cool Setpoint
                    </span>
                      </>
                    )}
                  </>)}
                  </div>
            </div>

            <div className="dropdownHolder relative group ">
              <Button
                data-testid={`unit-${serialNumber}-actions-toggle`}
                onClick={() => {
                  setSelectedUnits(props?.info);
                  setActiveDropdownId(props?.info.serialNumber);
                }}
                className="flex items-center gap-2 text-primaryText font-semibold text-sm leading-6 tracking-[0.021px]"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle cx="5" cy="12" r="2" fill="#FF8C65" />
                  <circle cx="12" cy="12" r="2" fill="#FF8C65" />
                  <circle cx="19" cy="12" r="2" fill="#FF8C65" />
                </svg>
              </Button>

              {props?.info.serialNumber === activeDropdownId && (
                <div
                  className="dropdownContent min-w-24 text-nowrap absolute bottom-full right-0 bg-[#FFF4D1] dark:bg-darkThemeDropdownBg dark:shadow-darkThemeDropdownMenuShadow rounded-lg shadow-appShadow cursor-pointer"
                  ref={dropdownRef}
                  data-testid={`unit-${serialNumber}-actions-menu`}
                >
                  <ul className="flex flex-col">
                    <li className="border-b border-grey dark:border-darkThemeBorder last:border-0">
                      <Button
                        data-testid={`unit-${serialNumber}-edit`}
                        onClick={() => handleEditUnit(props?.info)}
                        className={`w-full text-left text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded ${
                          isActionsDisabled ? "cursor-not-allowed opacity-50" : ""
                        }`}
                        disabled={isActionsDisabled}
                      >
                        Edit
                      </Button>
                    </li>
                    <li className="border-b border-grey dark:border-darkThemeBorder last:border-0">
                      <Button
                        data-testid={`unit-${serialNumber}-generate-report`}
                        onClick={() => handleGenerateReport?.(props?.info)}
                        className="w-full text-left text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded"
                      >
                        Generate Report
                      </Button>
                    </li>
                    <li className="border-b border-grey dark:border-darkThemeBorder last:border-0">
                      <Button
                        data-testid={`unit-${serialNumber}-remove-from-zone`}
                        onClick={() => handleRemoveUnit(props?.info)}
                        className={`w-full text-left text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded ${
                          isActionsDisabled ? "cursor-not-allowed opacity-50" : ""
                        }`}
                        disabled={isActionsDisabled}
                      >
                        Remove from zone
                      </Button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewUnitCardComponent;
